import React, { useEffect, useRef, useState } from 'react';

import { UseOutletContextCompany } from './Company';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { AccountingAccountDA } from '../../../services/admin/accounting.account.service';
import { Deadline } from '../../../models/deadline.model';
import { Unit } from '../../../models/unit.model';
import { Tax } from '../../../models/tax.model';
import { Feature } from '../../../models/feature.model';
import { Names } from '../../../models/names.model';
import { uploadTooltip } from '../../../utils/tooltip.util';
import { colorTemplateDescription } from '../../../libraries/template.color.library';
import { countryName } from '../../../libraries/country.library';
import { languageValue } from '../../../libraries/language.library';
import { qrTypeValue } from '../../../libraries/qr.type.library';
import { formatCurrency, formatDeadline, formatNumberAddress } from '../../../scripts/format.value.script';
import { taxMethodCompanyValue } from '../../../libraries/tax.method.company.library';
import { taxDeclarationCompanyValue } from '../../../libraries/tax.declaration.company.library';
import { dateFormat } from '../../../scripts/date.time.format.script';

import AppPreloader from '../../../components/Preloader';
import UuidBadge from '../../../components/element/UuidBadge';
import AppModalUuidQr from '../../../components/modal/ModalUuidQr';

export interface CompanyViewPageProps {};

const CompanyViewPage: React.FunctionComponent<CompanyViewPageProps> = props => {
  const {setRoute, company: companyForUser, settingsForCompany} = UseOutletContextCompany()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [acctSettings, setAcctSettings] = useState<any>(null)
  const [acctSettingsLoading, setAcctSettingsLoading] = useState(true)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function listTaxes(tax_fees: Tax[], language: string): string[] {
    return tax_fees.filter((item) => (item.active)).map((item) => (item.label[language.toLowerCase() as keyof Names]))
  }

  function listDeadlines(payment_deadlines: Deadline[]): string[] {
    return payment_deadlines.map((item) => (formatDeadline(lang, item)))
  }

  function listFeaturesForService(features: Feature[]): string[] {
    return features.filter((item) => (item.type === 'service')).map((item) => (item.name.toLowerCase()))
  }

  function listFeaturesForProduct(features: Feature[]): string[] {
    return features.filter((item) => (item.type === 'product')).map((item) => (item.name.toLowerCase()))
  }

  function listUnitsForService(measure_units: Unit[]): string[] {
    return measure_units.filter((item) => (item.type === 'service')).map((item) => (`${item.symbol} (${item.name.toLowerCase()})`))
  }

  function listUnitsForProduct(measure_units: Unit[]): string[] {
    return measure_units.filter((item) => (item.type === 'product')).map((item) => (`${item.symbol} (${item.name.toLowerCase()})`))
  }

  useEffect( () => {
    if (!companyForUser?.id) return

    let cancelled = false
    let attempts = 0
    const maxAttempts = 3
    const retryDelay = 3000

    const fetchAcctSettings = async () => {
      try {
        const response = await AccountingAccountDA.getVatRegime(companyForUser.id)
        if (cancelled) return

        if (response.status === 200 && response.data && response.data.subject_tax !== undefined) {
          setAcctSettings(response.data)
          setAcctSettingsLoading(false)
          return
        }
      } catch {
        if (cancelled) return
      }

      attempts++
      if (attempts < maxAttempts && !cancelled) {
        retryTimerRef.current = setTimeout(fetchAcctSettings, retryDelay)
      } else if (!cancelled) {
        setAcctSettingsLoading(false)
      }
    }

    setAcctSettings(null)
    setAcctSettingsLoading(true)
    fetchAcctSettings()

    return () => {
      cancelled = true
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser?.id])

  useEffect( () => {
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.companies, branch: lang.labels.viewCompany}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {companyForUser && settingsForCompany ? (
        <>
          <div className="d-flex flex-column flex-lg-row">
            <div className="flex-lg-row-fluid me-lg-5 order-1">
              <div className="card card-flush mb-5">
                <div className="card-body">
                  <div className="mb-10">
                    <h4 className="mb-5">{lang.labels.companyIdentity}:</h4>
                    <table className="table gs-0 gy-2 m-0">
                      <tbody>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.name}:</td>
                          <td className="min-w-200px fw-bold fs-6">{companyForUser.name}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.uuidCode}:</td>
                          <td className="min-w-200px fw-bold fs-6">
                            <UuidBadge uuid={companyForUser.uuid} label={companyForUser.name} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-10">
                    <h4 className="mb-5">{lang.labels.companyAddress}:</h4>
                    <table className="table gs-0 gy-2 m-0">
                      <tbody>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.street}:</td>
                          <td className="min-w-200px fw-bold fs-6">{companyForUser.address?.street}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.number}:</td>
                          <td className="min-w-200px fw-bold fs-6">{formatNumberAddress(lang, companyForUser.address?.number)}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.additional}:</td>
                          <td className="min-w-200px fw-bold fs-6">
                            {(companyForUser.address?.additional ?? '').length > 0 ? companyForUser.address?.additional : lang.labels.none}
                          </td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.administrativeArea}:</td>
                          <td className="min-w-200px fw-bold fs-6">{companyForUser.address?.administrative_area}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.zipCode}:</td>
                          <td className="min-w-200px fw-bold fs-6">{companyForUser.address?.zip_code}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.city}:</td>
                          <td className="min-w-200px fw-bold fs-6">{companyForUser.address?.city}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.country}:</td>
                          <td className="min-w-200px fw-bold fs-6">{countryName(lang.code, companyForUser.address?.country)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-10">
                    <h4 className="mb-5">{lang.labels.contactInformation}:</h4>
                    <table className="table gs-0 gy-2 m-0">
                      <tbody>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.email}:</td>
                          <td className="min-w-200px fw-bold fs-6">{companyForUser.email}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.phoneNumber}:</td>
                          <td className="min-w-200px fw-bold fs-6">{companyForUser.phone}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.webSite}:</td>
                          <td className="min-w-200px fw-bold fs-6">
                            {(companyForUser.website ?? '').length > 0 ? companyForUser.website : lang.labels.none}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-10">
                    <h4 className="mb-5">{lang.labels.legalInformation}:</h4>
                    <table className="table gs-0 gy-2 m-0">
                      <tbody>
                        <tr className="d-none">
                          <td className="w-200px fw-bold text-muted">{lang.labels.registerNumber}:</td>
                          <td className="min-w-200px fw-bold fs-6">
                            {(companyForUser.register_number ?? '').length > 0 ? companyForUser.register_number : lang.labels.none}
                          </td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.vatNumber}:</td>
                          <td className="min-w-200px fw-bold fs-6">
                            {(companyForUser.vat_number ?? '').length > 0 ? companyForUser.vat_number : lang.labels.none}
                          </td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.currency}:</td>
                          <td className="min-w-200px fw-bold fs-6">{formatCurrency(companyForUser.currency)}</td>
                        </tr>
                        {(() => {
                          const taxSrc: any = acctSettings ?? settingsForCompany
                          const subjectTax = taxSrc?.subject_tax ?? companyForUser?.subject_tax
                          const initLabel = lang.labels.accountingSettingsInitializing

                          return (
                            <>
                              <tr>
                                <td className="w-200px fw-bold text-muted">{lang.labels.vatAccountingType}:</td>
                                <td className="min-w-200px fw-bold fs-6">
                                  {acctSettingsLoading
                                    ? <span className="text-muted fst-italic">{initLabel}</span>
                                    : subjectTax
                                      ? taxMethodCompanyValue(lang, taxSrc?.tax_method ?? companyForUser?.tax_method ?? '')
                                      : lang.labels.none}
                                </td>
                              </tr>
                              <tr>
                                <td className="w-200px fw-bold text-muted">{lang.labels.declarationMethod}:</td>
                                <td className="min-w-200px fw-bold fs-6">
                                  {acctSettingsLoading
                                    ? <span className="text-muted fst-italic">{initLabel}</span>
                                    : subjectTax && (taxSrc?.tax_declaration ?? companyForUser?.tax_declaration ?? '').length > 0
                                      ? taxDeclarationCompanyValue(lang, taxSrc?.tax_declaration ?? companyForUser?.tax_declaration ?? '')
                                      : lang.labels.none}
                                </td>
                              </tr>
                              <tr>
                                <td className="w-200px fw-bold text-muted">{lang.labels.vatCalculationPeriod}:</td>
                                <td className="min-w-200px fw-bold fs-6">
                                  {acctSettingsLoading
                                    ? <span className="text-muted fst-italic">{initLabel}</span>
                                    : subjectTax && taxSrc?.current_tax_period?.name
                                      ? <>
                                          {taxSrc.current_tax_period.name}
                                          {taxSrc.current_tax_period.start_date && taxSrc.current_tax_period.end_date && (
                                            <span className="text-muted ms-2 fs-7">
                                              ({dateFormat(lang.code, taxSrc.current_tax_period.start_date)} – {dateFormat(lang.code, taxSrc.current_tax_period.end_date)})
                                            </span>
                                          )}
                                        </>
                                      : lang.labels.none}
                                </td>
                              </tr>
                              <tr>
                                <td className="w-200px fw-bold text-muted">{lang.labels.upcomingVatStatement}:</td>
                                <td className="min-w-200px fw-bold fs-6">
                                  {acctSettingsLoading
                                    ? <span className="text-muted fst-italic">{initLabel}</span>
                                    : subjectTax && taxSrc?.next_tax_statement
                                      ? dateFormat(lang.code, taxSrc.next_tax_statement)
                                      : lang.labels.none}
                                </td>
                              </tr>
                              {(taxSrc?.tax_method === 'tdfn' || taxSrc?.tax_method === 'taf') && (
                                <tr>
                                  <td className="w-200px fw-bold text-muted">{lang.labels.taxRate}:</td>
                                  <td className="min-w-200px fw-bold fs-6">
                                    {acctSettingsLoading
                                      ? <span className="text-muted fst-italic">{initLabel}</span>
                                      : (taxSrc?.tax_rate ?? '').length > 0
                                        ? `${taxSrc.tax_rate}%`
                                        : lang.labels.none}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td className="w-200px fw-bold text-muted">{lang.labels.fiscalYear}:</td>
                                <td className="min-w-200px fw-bold fs-6">
                                  {acctSettingsLoading
                                    ? <span className="text-muted fst-italic">{initLabel}</span>
                                    : (taxSrc?.fiscal_year_start ?? '').length > 0
                                      ? `${dateFormat(lang.code, taxSrc.fiscal_year_start)} – ${(taxSrc?.fiscal_year_end ?? '').length > 0 ? dateFormat(lang.code, taxSrc.fiscal_year_end) : '…'}`
                                      : lang.labels.none}
                                </td>
                              </tr>
                            </>
                          )
                        })()}
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-0">
                    <h4 className="mb-5">{lang.labels.downloadSettings}:</h4>
                    <table className="table gs-0 gy-2 m-0">
                      <tbody>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.language}:</td>
                          <td className="min-w-200px fw-bold fs-6 text-capitalize">{languageValue(lang, companyForUser.language)}</td>
                        </tr>
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.qrType}:</td>
                          <td className="min-w-200px fw-bold fs-6">{qrTypeValue(lang, settingsForCompany?.qr_type ?? '')}</td>
                        </tr>
                        <tr>
                          <td className="w-200px"></td>
                          <td className="min-w-200px"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-column flex-lg-row-auto w-lg-300px w-xl-350px order-2">
              <div className="card card-flush mb-5">
                <div className="card-header">
                  <div className="card-title">
                    <h4>{lang.labels.templateModel}</h4>
                  </div>
                </div>
                <div className="card-body text-center pt-0">
                  <div className="image-input image-input-empty image-input-outline image-input-placeholder mb-3">
                    <div className="image-input-wrapper w-150px h-175px">
                      <img src={settingsForCompany?.design_current?.thumbnail ?? ''} alt={lang.labels.template} className="w-100 h-100" />
                    </div>
                  </div>
                  <div className="text-muted fs-8">{lang.labels.designOfDocumentToDownloadQuotesAndInvoices}</div>
                </div>
              </div>
              {(settingsForCompany?.colors ?? []).length > 0 && (
                <div className="card card-flush mb-5">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.templateColors}</h4>
                    </div>
                  </div>
                  <div className="card-body text-center pt-0">
                    <div className={`row row-cols-${(settingsForCompany?.colors ?? []).length} mb-3`}>
                      {(settingsForCompany?.colors ?? []).map((item, index) => {
                        return (
                          <div
                            key={index}
                            className="col"
                            data-bs-toggle="tooltip"
                            data-bs-trigger="hover"
                            data-bs-original-title={colorTemplateDescription(lang, index)}
                          >
                            <label
                              className="border border-dashed border-dark rounded d-flex p-5"
                              style={{ backgroundColor: `${item}` }}
                            ></label>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-muted fs-8">{lang.labels.colorCombinationForDesignOfDocumentToDownload}</div>
                  </div>
                </div>
              )}
              {(settingsForCompany?.payment_deadlines ?? []).length > 0 && (
                <div className="card card-flush mb-5">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.dueDates}</h4>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <div className="d-flex flex-wrap text-muted">
                      {listDeadlines(settingsForCompany?.payment_deadlines ?? []).map((item, index) => {
                        return (
                          <div key={index} className="d-flex align-items-center w-125px text-truncate me-5">
                            <span className="bullet bg-primary me-3"></span>
                            {item}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              {(settingsForCompany?.features ?? []).length > 0 && (
                <div className="card card-flush mb-5">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.characteristicsForServiceProduct}</h4>
                    </div>
                  </div>
                  <div className="card-body row pt-0">
                    {listFeaturesForService(settingsForCompany?.features ?? []).length > 0 && (
                      <div className="flex-equal">
                        <div className="fw-bolder text-secondary ms-2 mb-2">{lang.labels.services}</div>
                        <div className="d-flex flex-column text-muted">
                          {listFeaturesForService(settingsForCompany?.features ?? []).map((item, index) => {
                            return (
                              <div key={index} className="d-flex align-items-center text-truncate">
                                <span className="bullet bg-primary me-3"></span>
                                {item}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {listFeaturesForProduct(settingsForCompany?.features ?? []).length > 0 && (
                      <div className="flex-equal">
                        <div className="fw-bolder text-secondary ms-2 mb-2">{lang.labels.products}</div>
                        <div className="d-flex flex-column text-muted">
                          {listFeaturesForProduct(settingsForCompany?.features ?? []).map((item, index) => {
                            return (
                              <div key={index} className="d-flex align-items-center text-truncate">
                                <span className="bullet bg-primary me-3"></span>
                                {item}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {(settingsForCompany?.measure_units ?? []).length > 0 && (
                <div className="card card-flush mb-5">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.unitsOfMeasure}</h4>
                    </div>
                  </div>
                  <div className="card-body row pt-0">
                    {listUnitsForService(settingsForCompany?.measure_units ?? []).length > 0 && (
                      <div className="flex-equal">
                        <div className="fw-bolder text-secondary ms-2 mb-2">{lang.labels.services}</div>
                        <div className="d-flex flex-column text-muted">
                          {listUnitsForService(settingsForCompany?.measure_units ?? []).map((item, index) => {
                            return (
                              <div key={index} className="d-flex align-items-center text-truncate">
                                <span className="bullet bg-primary me-3"></span>
                                {item}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {listUnitsForProduct(settingsForCompany?.measure_units ?? []).length > 0 && (
                      <div className="flex-equal">
                        <div className="fw-bolder text-secondary ms-2 mb-2">{lang.labels.products}</div>
                        <div className="d-flex flex-column text-muted">
                          {listUnitsForProduct(settingsForCompany?.measure_units ?? []).map((item, index) => {
                            return (
                              <div key={index} className="d-flex align-items-center text-truncate">
                                <span className="bullet bg-primary me-3"></span>
                                {item}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* <div className="card card-flush">
            <div className="card-header">
              <div className="card-title">
                <h4>{lang.labels.predefinedTaxesFees}</h4>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 row-cols-xxl-6">
                {listTaxes(settingsForCompany?.tax_fees ?? [], companyForUser?.language ?? 'fr').map((item, index) => {
                  return (
                    <div key={index} className="col d-flex align-items-center text-muted text-truncate p-0">
                      <span className="bullet bg-primary ms-5 me-2"></span>
                      {item}
                    </div>
                  );
                })}
              </div>
            </div>
          </div> */}
          <AppModalUuidQr uuid={companyForUser.uuid} label={companyForUser.name} />
        </>
      ) : (
        <AppPreloader></AppPreloader>
      )}
    </div>
  );
};

export default CompanyViewPage;
